import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import DestructiveConfirm from "../../components/common/DestructiveConfirm";
import {
  approveScopeDocument,
  createAuditFinding,
  getAudit,
  listAuditHistory,
  listScopeDocuments,
  transitionAuditStep,
  updateAuditScope,
  uploadScopeDocument,
  uploadFinalReport,
  rejectScopeDocument,
} from "../../api/audit.api";
import EmptyState from "../../components/common/EmptyState";
import Button from "../../ui/Button";
import PageState from "../../ui/PageState";
import RelatedKpisPanel from "../../components/kpi/RelatedKpisPanel";
import PinButton from "../../components/pins/PinButton";
import useRegisterRecentView from "../../features/recentViews/useRegisterRecentView";
import { useAuth } from "../../useAuth";
import { isRole, RoleEnum } from "../../constants/roles";
import Textarea from "../../ui/Textarea";
import { colors, radius, shadows, spacing, typography } from "../../ui/tokens";
import AuditRecommendationForm from "./AuditRecommendationForm";

const steps = [
  { key: "scope_draft", label: "Scope Draft" },
  { key: "scope_approval", label: "Scope Approval" },
  { key: "audit_start", label: "Audit Start" },
  { key: "investigation", label: "Investigation" },
  { key: "findings", label: "Recommendations" },
  { key: "draft_report", label: "Draft Report" },
  { key: "final_report", label: "Final Report" },
];
const finalStepKeys = new Set(["draft_report", "final_report"]);
const stepLabels = Object.fromEntries(steps.map((step) => [step.key, step.label]));

const stateColours = {
  completed: colors.success,
  in_progress: colors.brandPrimary,
  not_started: colors.textSubtle,
  blocked: colors.warning,
};

const shellStyle = {
  display: "grid",
  gap: spacing.xl,
};

const actionErrorStyle = {
  border: `1px solid ${colors.dangerBorder}`,
  background: colors.dangerSubtle,
  color: colors.danger,
  borderRadius: radius.md,
  padding: `${spacing.md + spacing.xs}px ${spacing.xl}px`,
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: spacing.xl,
  flexWrap: "wrap",
};

const titleStyle = {
  margin: 0,
  color: colors.text,
  fontSize: typography.size.xl,
  fontWeight: typography.weight.bold,
};

const subtitleStyle = {
  color: colors.textMuted,
  fontSize: typography.size.xs,
};

const workflowWrapStyle = {
  marginTop: spacing.xl,
  display: "grid",
  gap: spacing.sm,
};

const sectionStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  background: colors.surface,
  boxShadow: shadows.sm,
  padding: spacing.xl + spacing.sm,
  display: "grid",
  gap: spacing.md,
};

const sectionTitleStyle = {
  margin: 0,
  color: colors.text,
  fontSize: typography.size.lg,
  fontWeight: typography.weight.bold,
};

const sectionTextStyle = {
  margin: 0,
  color: colors.textMuted,
  fontSize: typography.size.sm,
  lineHeight: 1.55,
};

const uploadRowStyle = {
  display: "flex",
  gap: spacing.sm,
  alignItems: "center",
  flexWrap: "wrap",
};

const recordCardStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: `${spacing.md + spacing.xs}px ${spacing.xl}px`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.md,
  flexWrap: "wrap",
  background: colors.surfaceSubtle,
};

const extractApiError = (err, fallback) =>
  err?.response?.data?.detail || err?.detail || err?.message || fallback;

export default function AuditExecutionDetail({ auditId = null, inline = false, embedded = false }) {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const id = auditId ?? routeId;
  const queryClient = useQueryClient();
  const { user } = useAuth() || {};
  const isAdmin = isRole(user?.role, RoleEnum.ADMIN);
  const [scopeSummary, setScopeSummary] = useState("");
  const [confirmScopeAction, setConfirmScopeAction] = useState(null);
  const [confirmStep, setConfirmStep] = useState(null);
  const [actionError, setActionError] = useState("");

  const auditQuery = useQuery({
    queryKey: ["audit", id],
    queryFn: () => getAudit(id).then((res) => res.data),
    onSuccess: (data) => setScopeSummary(data?.scope_summary || ""),
    enabled: Boolean(id),
  });

  const docsQuery = useQuery({
    queryKey: ["audit-scope-docs", id],
    queryFn: () => listScopeDocuments(id).then((res) => res.data),
    enabled: Boolean(id),
  });

  const historyQuery = useQuery({
    queryKey: ["audit-history", id],
    queryFn: () => listAuditHistory(id).then((res) => res.data),
    enabled: Boolean(id),
  });

  const scopeMutation = useMutation({
    mutationFn: (payload) => updateAuditScope(id, payload, isAdmin),
    onMutate: () => setActionError(""),
    onSuccess: () => queryClient.invalidateQueries(["audit", id]),
    onError: (err) => setActionError(extractApiError(err, "Unable to save audit scope.")),
  });

  const stepMutation = useMutation({
    mutationFn: ({ targetStep, state }) => transitionAuditStep(id, targetStep, state, isAdmin),
    onMutate: () => setActionError(""),
    onSuccess: () => queryClient.invalidateQueries(["audit", id]),
    onError: (err) => setActionError(extractApiError(err, "Unable to update audit workflow.")),
  });

  const uploadMutation = useMutation({
    mutationFn: (file) => uploadScopeDocument(id, file),
    onSuccess: () => queryClient.invalidateQueries(["audit-scope-docs", id]),
  });

  const approveMutation = useMutation({
    mutationFn: ({ docId, status }) =>
      status === "Approved" ? approveScopeDocument(id, docId) : rejectScopeDocument(id, docId, "Rejected"),
    onSuccess: () => queryClient.invalidateQueries(["audit-scope-docs", id]),
  });

  const findingMutation = useMutation({
    mutationFn: (payload) => createAuditFinding(id, payload),
    onMutate: () => setActionError(""),
    onSuccess: () => {
      queryClient.invalidateQueries(["audit", id]);
    },
    onError: (err) => setActionError(extractApiError(err, "Unable to add audit recommendation.")),
  });

  const finalReportMutation = useMutation({
    mutationFn: (file) => uploadFinalReport(id, file),
    onSuccess: () => queryClient.invalidateQueries(["audit", id]),
  });

  const audit = auditQuery.data;
  const stepStates = audit?.step_states || {};
  const scopeLockedForUser = Boolean(audit?.scope_locked_at) && !isAdmin;
  useRegisterRecentView({
    itemType: "audit_engagement",
    itemId: audit?.id || id,
    title: audit?.title,
    secondaryText: audit?.current_step || audit?.status || null,
    enabled: Boolean(audit?.id && audit?.title),
  });

  const requestStepComplete = (step) => {
    if (finalStepKeys.has(step.key)) {
      setConfirmStep(step);
      return;
    }
    stepMutation.mutate({ targetStep: step.key, state: "completed" });
  };

  if (!id) return null;
  if (auditQuery.isLoading) {
    return (
      <PageState
        state="loading"
        align="left"
        title="Loading audit"
        description="Glyder is preparing workflow progress, scope materials, and recommendations for this audit."
      />
    );
  }
  if (auditQuery.isError) {
    return (
      <PageState
        state="error"
        align="left"
        title="Audit is unavailable"
        description={`${auditQuery.error?.message || "Glyder could not load this audit."} Refresh the workspace to try again.`}
      />
    );
  }

  return (
    <div style={shellStyle}>
      {!inline && !embedded && <Breadcrumbs items={[{ label: "Audits", href: "/audit/audits" }, { label: audit?.title }]} />}
      {actionError && (
        <div role="alert" style={actionErrorStyle}>
          {actionError}
        </div>
      )}
      <div style={headerStyle}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={titleStyle}>{audit?.title}</h1>
          <div style={subtitleStyle}>
            Status: {audit?.status} • Current Step: {stepLabels[audit?.current_step] || audit?.current_step}
          </div>
          <div style={workflowWrapStyle}>
            <div style={{ fontSize: typography.size.xs, fontWeight: typography.weight.medium, color: colors.textSecondary }}>
              Workflow
            </div>
            <div
              style={{
                display: "flex",
                gap: spacing.sm,
                flexWrap: "nowrap",
                overflowX: "auto",
                paddingBottom: spacing.xs,
              }}
            >
              {steps.map((step) => {
                const state = stepStates?.[step.key] || "not_started";
                const isCurrentStep = audit?.current_step === step.key;
                return (
                  <Button
                    key={step.key}
                    onClick={() => requestStepComplete(step)}
                    variant="secondary"
                    aria-current={isCurrentStep ? "step" : undefined}
                    style={{
                      borderRadius: radius.md,
                      padding: `${spacing.md + spacing.xs}px ${spacing.xl}px`,
                      flex: "0 0 auto",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      borderColor: isCurrentStep ? colors.brandPrimary : undefined,
                      background: isCurrentStep ? colors.tableRowSelected : undefined,
                      boxShadow: isCurrentStep ? `0 0 0 3px ${colors.focusRing}` : "none",
                    }}
                  >
                    <span style={{ fontWeight: 600, whiteSpace: "nowrap", color: isCurrentStep ? colors.brandPrimary : undefined }}>
                      {step.label}
                    </span>
                    <span style={{ color: isCurrentStep ? colors.brandPrimary : stateColours[state] || colors.textMuted, fontSize: typography.size.xs }}>
                      {isCurrentStep ? "Current stage" : state}
                    </span>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
          {!inline && (
            <PinButton
              itemType="audit_engagement"
              itemId={id}
              summary={{
                title: audit?.title || "Audit engagement",
                secondaryText: audit?.current_step ? `Step: ${stepLabels[audit.current_step] || audit.current_step}` : null,
                status: audit?.status || null,
                ownerDisplay: audit?.audit_lead_id || null,
                updatedAt: audit?.updated_at || null,
              }}
            />
          )}
          <StatusPill value={audit?.status} />
        </div>
      </div>

      <section style={sectionStyle}>
        <RelatedKpisPanel entityType="audit" entityId={id} />
      </section>

      <section style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Scope</h3>
        <Textarea
          value={scopeSummary}
          onChange={(e) => setScopeSummary(e.target.value)}
          rows={4}
          style={{ width: "100%", minHeight: 120 }}
          disabled={scopeLockedForUser}
          placeholder="Scope summary"
        />
        <div style={uploadRowStyle}>
          <Button
            onClick={() => scopeMutation.mutate({ scope_summary: scopeSummary })}
            disabled={scopeLockedForUser}
            variant="primary"
          >
            Save Scope
          </Button>
          {audit.scope_locked_at && (
            <span style={{ color: isAdmin ? colors.brandPrimary : colors.danger, fontSize: typography.size.xs }}>
              {isAdmin ? "Scope locked; admin override enabled" : "Scope locked"}
            </span>
          )}
        </div>
      </section>

      <section style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Scope Documents</h3>
        <div style={uploadRowStyle}>
          <input type="file" onChange={(e) => e.target.files?.[0] && uploadMutation.mutate(e.target.files[0])} />
          {uploadMutation.isLoading && <span style={sectionTextStyle}>Uploading...</span>}
        </div>
        {docsQuery.isLoading && (
          <PageState
            state="loading"
            align="left"
            compact
            title="Loading scope documents"
            description="Preparing the latest scope document versions and approval state."
          />
        )}
        {!docsQuery.isLoading && docsQuery.data?.length === 0 && <EmptyState title="No scope documents" />}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {docsQuery.data?.map((doc) => (
            <div key={doc.id} style={recordCardStyle}>
              <div>
                <div style={{ fontWeight: 600 }}>
                  v{doc.version} - {doc.filename}
                </div>
                <div style={subtitleStyle}>
                  Status: {doc.status} • Uploaded by {doc.uploaded_by || "unknown"}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Button onClick={() => setConfirmScopeAction({ doc, status: "Approved" })} variant="primary">
                  Approve
                </Button>
                <Button onClick={() => setConfirmScopeAction({ doc, status: "Rejected" })} variant="destructive">
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Finding Actions and Recommendations</h3>
        <p style={sectionTextStyle}>
          Add recommendations here. Each one is created as a child action under this audit in the Actions module.
        </p>
        <AuditRecommendationForm
          auditTitle={audit?.title}
          defaultOwner={audit?.audit_lead_id || ""}
          loading={findingMutation.isLoading}
          onSubmit={(payload) => findingMutation.mutate(payload)}
        />
        {audit?.findings?.length ? (
          <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
            {audit.findings.map((f) => (
              <div key={f.id} style={recordCardStyle}>
                <div style={{ display: "grid", gap: 4 }}>
                  <div style={{ fontWeight: 600 }}>{f.title}</div>
                  <div style={subtitleStyle}>
                    {f.severity} • {f.status}
                  </div>
                </div>
                {f.linked_action_id ? (
                  <Button variant="secondary" onClick={() => navigate(`/actions/${f.linked_action_id}`)}>
                    Open Action
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState title="No recommendations linked" />
        )}
      </section>

      <section style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Final Report</h3>
        <div style={uploadRowStyle}>
          <input type="file" accept="application/pdf" onChange={(e) => e.target.files?.[0] && finalReportMutation.mutate(e.target.files[0])} />
          {finalReportMutation.isLoading && <span style={sectionTextStyle}>Uploading...</span>}
          {audit.final_report_filename && (
            <span style={subtitleStyle}>
              Uploaded: {audit.final_report_filename}
            </span>
          )}
        </div>
      </section>
      <section style={sectionStyle}>
        <h3 style={sectionTitleStyle}>History</h3>
        {historyQuery.isLoading && (
          <PageState
            state="loading"
            align="left"
            compact
            title="Loading audit history"
            description="Preparing workflow and evidence activity for this audit."
          />
        )}
        {!historyQuery.isLoading && (historyQuery.data?.length || 0) === 0 && <EmptyState title="No history yet" />}
        {historyQuery.data?.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 4 }}>
            {historyQuery.data.map((entry) => (
              <li key={entry.id} style={{ fontSize: typography.size.sm, color: colors.textSecondary }}>
                {entry.event_type} • {entry.changed_at}
              </li>
            ))}
          </ul>
        )}
      </section>
      <DestructiveConfirm
        actionId={confirmScopeAction?.status === "Approved" ? "AUDIT_SCOPE_DOCUMENT_APPROVE" : "AUDIT_SCOPE_DOCUMENT_REJECT"}
        open={Boolean(confirmScopeAction)}
        resourceDisplayName={confirmScopeAction?.doc?.filename}
        context={
          confirmScopeAction
            ? { scopeLabel: `${confirmScopeAction.status === "Approved" ? "Approve" : "Reject"} scope document` }
            : undefined
        }
        onCancel={() => setConfirmScopeAction(null)}
        onConfirm={() => {
          const pending = confirmScopeAction;
          setConfirmScopeAction(null);
          if (pending) {
            approveMutation.mutate({ docId: pending.doc.id, status: pending.status });
          }
        }}
      />
      <DestructiveConfirm
        actionId="AUDIT_STEP_COMPLETE"
        open={Boolean(confirmStep)}
        resourceDisplayName={confirmStep?.label}
        context={{ scopeLabel: "Mark final step completed" }}
        onCancel={() => setConfirmStep(null)}
        onConfirm={() => {
          const pending = confirmStep;
          setConfirmStep(null);
          if (pending) {
            stepMutation.mutate({ targetStep: pending.key, state: "completed" });
          }
        }}
      />
    </div>
  );
}

function StatusPill({ value }) {
  if (!value) return null;
  const tone = value === "Complete" ? colors.success : value === "Active" ? colors.brandPrimary : colors.textSecondary;
  return (
    <span
      style={{
        padding: "6px 10px",
        borderRadius: radius.pill,
        background: tone,
        color: "#fff",
        fontSize: typography.size.xs,
      }}
    >
      {value}
    </span>
  );
}
